import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { ProgramVersionDto } from '../../../api/types/dtos/program';
import { useUpdateProgramVersion } from '../../../api/hooks/useProgramVersions';
import { useOrganization } from '../../../contexts/OrganizationContext';

interface EditVersionModalProps {
  open: boolean;
  onClose: () => void;
  version: ProgramVersionDto | null;
  programId: string;
}

const EditVersionModal: React.FC<EditVersionModalProps> = ({
  open,
  onClose,
  version,
  programId,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [documentUrl, setDocumentUrl] = useState('');
  const [website, setWebsite] = useState('');
  
  const { selectedOrganization } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const updateMutation = useUpdateProgramVersion(
    selectedOrganization?.id || '',
    programId
  );

  useEffect(() => {
    if (version && open) {
      setName(version.name);
      setDescription(version.description || '');
      setDocumentUrl(version.documentUrl || '');
      setWebsite(version.website || '');
    }
  }, [version, open]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      enqueueSnackbar('El nombre es requerido', { variant: 'error' });
      return;
    }

    if (!version?.id) {
      enqueueSnackbar('No se puede actualizar la versión', { variant: 'error' });
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: version.id,
        data: {
          name: name.trim(),
          description: description.trim() || undefined,
          programId,
          documentUrl: documentUrl.trim() || undefined,
          website: website.trim() || undefined,
        },
      });

      enqueueSnackbar('Versión actualizada exitosamente', { variant: 'success' });
      handleClose();
    } catch (error) {
      enqueueSnackbar('Error al actualizar la versión', { variant: 'error' });
      console.error('Error updating version:', error);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setDocumentUrl('');
    setWebsite('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Editar Versión</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Nombre"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Descripción"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            label="URL del Documento (opcional)"
            value={documentUrl}
            onChange={(e) => setDocumentUrl(e.target.value)}
            fullWidth
            placeholder="https://..."
          />
          <TextField
            label="Sitio Web (opcional)"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            fullWidth
            placeholder="https://..."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={updateMutation.isPending}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={updateMutation.isPending}
          startIcon={updateMutation.isPending && <CircularProgress size={20} />}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditVersionModal;

